import React, { useState, useEffect } from 'react';
import { FaTimes, FaFilter, FaSpinner } from 'react-icons/fa';
import SearchFilters from '../SearchFilters/SearchFilters';
import QuickFilters from '../QuickFilters/QuickFilters';
import { useLanguage } from '../../App';
import './FilterDialog.css';

const FilterDialog = ({
  isOpen,
  onClose,
  type = 'projects', // 'projects' or 'units'
  filters = {},
  onApplyFilters,
  isLoading = false
}) => {
  const { language } = useLanguage();
  const [localFilters, setLocalFilters] = useState(filters);
  const [selectedQuickFilters, setSelectedQuickFilters] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalFilters(filters);
    setSelectedQuickFilters([]);
    setHasChanges(false);
  }, [filters, isOpen]);

  const handleFiltersChange = (newFilters) => {
    setLocalFilters(newFilters);
    setHasChanges(true);
  };



  const handleQuickFilterSelect = (filterId, quickFilters) => {
    let newSelectedFilters;
    let combinedFilters = { ...localFilters };

    if (selectedQuickFilters.includes(filterId)) {
      // Remove filter
      newSelectedFilters = selectedQuickFilters.filter(id => id !== filterId);
      // For simplicity, we'll clear all filters when deselecting quick filters
      if (newSelectedFilters.length === 0) {
        combinedFilters = {};
      }
    } else {
      // Add filter
      newSelectedFilters = [...selectedQuickFilters, filterId];
      combinedFilters = { ...combinedFilters, ...quickFilters };
    }

    setSelectedQuickFilters(newSelectedFilters);
    setLocalFilters(combinedFilters);
    setHasChanges(true);
  };

  const handleApply = () => {
    if (onApplyFilters) {
      onApplyFilters({
        filters: localFilters
      });
    }
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalFilters({});
    setSelectedQuickFilters([]);
    setHasChanges(true);
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmClose = window.confirm(
        language === 'ar' 
          ? 'لديك تغييرات غير محفوظة. هل تريد المتابعة؟'
          : 'You have unsaved changes. Do you want to continue?'
      );
      if (!confirmClose) return;
    }
    onClose();
  };

  if (!isOpen) return null;

  const getActiveFiltersCount = () => {
    return Object.values(localFilters).filter(value => 
      value !== null && value !== undefined && value !== '' && 
      (Array.isArray(value) ? value.length > 0 : true)
    ).length;
  };

  return (
    <div className="filter-dialog-overlay" onClick={handleClose}>
      <div 
        className={`filter-dialog ${language === 'ar' ? 'rtl' : 'ltr'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="filter-dialog-header">
          <div className="filter-dialog-title">
            <FaFilter className="title-icon" />
            <span>{language === 'ar' ? 'الفلاتر' : 'Filters'}</span>
            {getActiveFiltersCount() > 0 && (
              <span className="active-count">({getActiveFiltersCount()})</span>
            )}
          </div>
          <button className="close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="filter-dialog-content">
          {/* Quick Filters */}
          <div className="filter-section">
            <QuickFilters
              type={type}
              selectedFilters={selectedQuickFilters}
              onQuickFilterSelect={handleQuickFilterSelect}
            />
          </div>



          {/* Advanced Filters */}
          <div className="filter-section">
            <SearchFilters
              type={type}
              filters={localFilters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={() => {
                setLocalFilters({});
                setSelectedQuickFilters([]);
                setHasChanges(true);
              }}
              isVisible={true}
              onToggle={() => {}}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="filter-dialog-footer">
          <button 
            className="reset-btn"
            onClick={handleReset}
            disabled={isLoading}
          >
            {language === 'ar' ? 'إعادة تعيين' : 'Reset'}
          </button>
          
          <div className="action-buttons">
            <button 
              className="cancel-btn"
              onClick={handleClose}
              disabled={isLoading}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button 
              className={`apply-btn ${hasChanges ? 'has-changes' : ''}`}
              onClick={handleApply}
              disabled={isLoading || !hasChanges}
            >
              {isLoading ? (
                <>
                  <FaSpinner className="spinner" />
                  {language === 'ar' ? 'جاري التطبيق...' : 'Applying...'}
                </>
              ) : (
                <>
                  {language === 'ar' ? 'تطبيق' : 'Apply'}
                  {hasChanges && <span className="changes-indicator">•</span>}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterDialog;
